import RGL from "react-grid-layout";

import { DecodedWidgetConfig } from "~/pages/GoogleDashboardPage/types/DecodedWidgetConfig.type";
import { RenderableWidgetOrError } from "~/pages/GoogleDashboardPage/types/RenderableWidget.type";

export const useLayout = ({
  widgets,
  updateWidgets,
}: {
  widgets: RenderableWidgetOrError[];
  updateWidgets: (widgets: DecodedWidgetConfig[]) => Promise<void>;
}) => {
  const layout = widgets.map(
    (widget): RGL.Layout => ({
      i: widget.id,
      ...widget.config.metadata.placement,
      maxH: 2,
    }),
  );

  const onLayoutChange = (newLayout: RGL.Layout[]) => {
    // react-grid-layout fires onLayoutChange on mount (with the normalized
    // initial layout) and again whenever the `layout` prop changes — not
    // just on user-initiated drag/resize. Without this guard, the mount-
    // time call triggers a PATCH → invalidates the dashboards query →
    // refetch → re-render → another no-op layout → another PATCH.
    // Compare against the current placements and bail out on no-op.
    const hasChanges = widgets.some((widget) => {
      const next = newLayout.find((item) => item.i === widget.id);

      if (!next) return false;

      const current = widget.config.metadata.placement;

      return (
        next.x !== current.x ||
        next.y !== current.y ||
        next.w !== current.w ||
        next.h !== current.h
      );
    });

    if (!hasChanges) return;

    updateWidgets(
      widgets.map((widget) => {
        const newPlacement = newLayout.find(
          (layoutItem) => layoutItem.i === widget.id,
        );

        if (!newPlacement) {
          return widget.config;
        }

        const { x, y, w, h } = newPlacement;

        return {
          ...widget.config,
          metadata: {
            ...widget.config.metadata,
            placement: { x, y, w, h },
          },
        };
      }),
    );
  };

  return {
    layout,
    onLayoutChange,
  };
};
